import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TtsFlowPreviewPane } from '@/features/tts-flow/components/steps/TtsFlowPreviewPane';
import type { TtsFlowFormValues, TtsRow } from '@/features/tts-flow/types';
import { ContinuousScheduleCard } from '@/features/voice-campaigns/components/ContinuousScheduleCard';
import { formatDate, formatDateTime } from '@/lib/utils/date';
import {
  ArrowLeft,
  Hash,
  Loader2,
  PhoneIncoming,
  SkipForward,
  UploadCloud,
  Zap,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCampaignInfo, type CampaignInfoResponse } from '../api';
import { templateLabel } from '../types';

function DataRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[160px_1fr] gap-3 border-t border-border py-2 text-sm first:border-t-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-foreground">
        {value || <span className="text-muted-foreground">—</span>}
      </dd>
    </div>
  );
}

const LANGUAGE_LABEL: Record<string, string> = {
  'en-IN': 'English',
  'hi-IN': 'Hindi',
};

function RateRing({
  value,
  label,
  sub,
  stroke,
}: {
  value: number;
  label: string;
  sub: string;
  stroke: string;
}) {
  const r = 28;
  const circ = 2 * Math.PI * r;
  const pct = Math.min(Math.max(value, 0), 100);
  return (
    <div className="flex items-center gap-3">
      <div className="relative h-[72px] w-[72px]">
        <svg width="72" height="72" viewBox="0 0 72 72" className="-rotate-90">
          <circle cx="36" cy="36" r={r} fill="none" strokeWidth="7" className="stroke-muted" />
          <circle
            cx="36"
            cy="36"
            r={r}
            fill="none"
            strokeWidth="7"
            strokeLinecap="round"
            strokeDasharray={circ}
            strokeDashoffset={circ * (1 - pct / 100)}
            className={stroke}
            style={{ transition: 'stroke-dashoffset 0.8s ease' }}
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold tabular-nums">
          {Math.round(pct)}%
        </span>
      </div>
      <div>
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">{sub}</div>
      </div>
    </div>
  );
}

export function VoiceCampaignInfoPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const info = useCampaignInfo(id ?? null);
  // Summary is shown only when opened from the dashboard's Recent Campaigns.
  const [searchParams] = useSearchParams();
  const showSummary = searchParams.get('from') === 'dashboard';

  const [previewRows, setPreviewRows] = useState<TtsRow[] | null>(null);

  const sortedTtsRows = useMemo(() => {
    const raw = (info.data as CampaignInfoResponse | undefined)?.ttsRows ?? [];
    return raw.slice().sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
  }, [info.data]);

  const ttsValues = useMemo<TtsFlowFormValues>(() => {
    const d = info.data as CampaignInfoResponse | undefined;
    const rows: TtsRow[] = sortedTtsRows.map((row) => ({
      ttsType: row.ttsType === 'variable' ? 'variable' : 'prompt',
      value: String(row.value ?? ''),
      variableType: row.variableType ?? '',
    }));
    return {
      ttsFlowName: d?.campaign.campaignName ?? '',
      language: d?.campaign.language ?? '',
      gender: d?.campaign.gender ?? '',
      ttsRows: previewRows ?? rows,
    } as TtsFlowFormValues;
  }, [info.data, sortedTtsRows, previewRows]);

  if (info.isLoading || !info.data) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading campaign…
      </div>
    );
  }

  const data: CampaignInfoResponse = info.data;
  const c = data.campaign;
  const templateId = Number(c.templateId);
  const isTts = templateId === 7 || templateId === 8 || templateId === 9;
  const isCustomIvr = templateId === 3;
  const langLabel = c.language ? (LANGUAGE_LABEL[c.language] ?? c.language) : '';
  const uploaded = Number(c.numbersUploaded ?? 0);
  const skipped = Number(c.skippedCount ?? 0);
  const dnd = Number(c.dndCount ?? 0);
  const dialed = Number(c.numbersProcessed ?? 0);
  const pending = Math.max(uploaded - skipped - dnd - dialed, 0);
  const connected = Number(c.callsConnected ?? 0);
  const dtmf = Number(c.dtmfCount ?? 0);
  const pulses = Number(c.pulses ?? 0);
  const answerRate = dialed > 0 ? (connected / dialed) * 100 : 0;
  const dtmfRate = connected > 0 ? (dtmf / connected) * 100 : 0;
  const segments = [
    { key: 'dialed', label: 'Dialed', value: dialed, bar: 'bg-emerald-500', dot: 'bg-emerald-500' },
    { key: 'pending', label: 'Pending', value: pending, bar: 'bg-amber-400', dot: 'bg-amber-400' },
    { key: 'dnd', label: 'DND', value: dnd, bar: 'bg-rose-500', dot: 'bg-rose-500' },
    { key: 'skipped', label: 'Skipped', value: skipped, bar: 'bg-slate-400', dot: 'bg-slate-400' },
  ];

  return (
    <ScrollPage>
      <PageHeader
        title={c.campaignName || `Campaign #${id}`}
        description={`${templateLabel(templateId)} · ID #${id}`}
        actions={
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        }
      />

      {showSummary && (
        <Card className="overflow-hidden border-primary/20 bg-gradient-to-br from-primary/5 via-transparent to-transparent">
          <CardContent className="space-y-6 p-6">
            {/* Hero: total + rings */}
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  <UploadCloud className="h-4 w-4" />
                  Campaign summary
                </div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-4xl font-bold tabular-nums">
                    {uploaded.toLocaleString()}
                  </span>
                  <span className="text-sm text-muted-foreground">numbers uploaded</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-6">
                <RateRing
                  value={answerRate}
                  label="Answer rate"
                  sub={`${connected.toLocaleString()} of ${dialed.toLocaleString()} dialed`}
                  stroke="stroke-emerald-500"
                />
                <RateRing
                  value={dtmfRate}
                  label="DTMF response"
                  sub={`${dtmf.toLocaleString()} of ${connected.toLocaleString()} connected`}
                  stroke="stroke-sky-500"
                />
              </div>
            </div>

            {/* Where every uploaded number went */}
            <div>
              <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted">
                {uploaded > 0 &&
                  segments.map((s) =>
                    s.value > 0 ? (
                      <div
                        key={s.key}
                        className={`${s.bar} transition-all duration-700`}
                        style={{ width: `${(s.value / uploaded) * 100}%` }}
                        title={`${s.label}: ${s.value.toLocaleString()}`}
                      />
                    ) : null,
                  )}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
                {segments.map((s) => (
                  <div key={s.key} className="flex items-center gap-2 text-sm">
                    <span className={`h-2.5 w-2.5 rounded-full ${s.dot}`} />
                    <span className="text-muted-foreground">{s.label}</span>
                    <span className="ml-auto font-semibold tabular-nums">
                      {s.value.toLocaleString()}
                    </span>
                    <span className="w-10 text-right text-xs tabular-nums text-muted-foreground">
                      {uploaded > 0 ? `${Math.round((s.value / uploaded) * 100)}%` : '—'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Call outcome tiles */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                {
                  label: 'Skipped numbers',
                  value: skipped,
                  icon: SkipForward,
                  tone: 'text-slate-500 bg-slate-500/10',
                },
                {
                  label: 'Connected calls',
                  value: connected,
                  icon: PhoneIncoming,
                  tone: 'text-emerald-600 bg-emerald-500/10',
                },
                {
                  label: 'DTMF pressed',
                  value: dtmf,
                  icon: Hash,
                  tone: 'text-sky-600 bg-sky-500/10',
                },
                {
                  label: 'Pulses used',
                  value: pulses,
                  icon: Zap,
                  tone: 'text-violet-600 bg-violet-500/10',
                },
              ].map((t) => (
                <div
                  key={t.label}
                  className="flex items-center gap-3 rounded-lg border border-border bg-card/60 p-3"
                >
                  <span className={`flex h-9 w-9 items-center justify-center rounded-md ${t.tone}`}>
                    <t.icon className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="text-lg font-semibold tabular-nums leading-tight">
                      {t.value.toLocaleString()}
                    </div>
                    <div className="text-xs text-muted-foreground">{t.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Basics</CardTitle>
          </CardHeader>
          <CardContent>
            <dl>
              <DataRow label="Name" value={c.campaignName} />
              <DataRow label="Type" value={templateLabel(templateId)} />
              {!isTts && !isCustomIvr && <DataRow label="Valid DTMF" value={c.dtmf} />}
              {c.repeatMenu && <DataRow label="Repeat menu" value={c.repeatMenu} />}
              {!isCustomIvr && <DataRow label="Menu wait time" value={c.menuWaitTime} />}
              {!isCustomIvr && (
                <>
                  <DataRow label="No. of Retries" value={`${c.retries ?? 0}`} />
                  <DataRow label="Retry Interval (Mins)" value={`${c.retryInterval ?? 0}`} />
                </>
              )}
              {Number(c.ringDuration) > 0 && (
                <DataRow label="Ring Duration" value={`${c.ringDuration} seconds`} />
              )}
              {isTts && (
                <>
                  <DataRow label="Language" value={langLabel} />
                  <DataRow label="Voice gender" value={c.gender} />
                  {c.ttsflowName && <DataRow label="TTS Flow Name" value={c.ttsflowName} />}
                </>
              )}
              {data.ivrList?.[0]?.ivrName && (
                <DataRow
                  label="Custom IVR"
                  value={
                    data.ivrList?.[0]?.ivrName ? (
                      <Link
                        to={`/ivr/${data.ivrList[0].ivrId}?view=1`}
                        className="font-medium text-primary hover:underline"
                      >
                        {data.ivrList[0].ivrName} (#{data.ivrList[0].ivrId})
                      </Link>
                    ) : (
                      '—'
                    )
                  }
                />
              )}
              {Number(c.continuous) === 1 ? (
                <>
                  <DataRow label="Scheduled" value={formatDateTime(c.scheduledAt ?? c.scheduleTime)} />
                  <DataRow label="Started" value={formatDate(c.startDate, 'MMM dd, yyyy')} />
                  <DataRow label="Ended" value={formatDate(c.endDate, 'MMM dd, yyyy')} />
                </>
              ) : (
                <>
                  {c.sendLater && (
                    <DataRow label="Scheduled" value={formatDateTime(c.scheduledAt ?? c.scheduleTime)} />
                  )}
                  <DataRow label="Start Time" value={formatDateTime(c.scheduleTime)} />
                  <DataRow label="End Time" value={formatDateTime(c.endDate)} />
                </>
              )}
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Routing</CardTitle>
            <CardDescription>Locations and CLIs the campaign dialed from.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="text-xs font-medium text-muted-foreground">
                Locations ({data.locationList?.length ?? 0})
              </div>
              {(data.locationList ?? []).length === 0 ? (
                <div className="mt-1 text-sm text-muted-foreground">—</div>
              ) : (
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {(data.locationList ?? []).map((l, i) => (
                    <Badge key={i} variant="outline" className="font-normal">
                      {l.locationName || l.location}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className="text-xs font-medium text-muted-foreground">
                CLIs ({data.cliList?.length ?? 0})
              </div>
              {(data.cliList ?? []).length === 0 ? (
                <div className="mt-1 text-sm text-muted-foreground">Shared pool</div>
              ) : (
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {(data.cliList ?? []).map((c, i) => (
                    <Badge key={i} variant="outline" className="font-mono text-xs">
                      {c.cli}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {(Boolean(data.scheduleList?.scheduleJson) || Boolean(c.continuous)) && (
        <ContinuousScheduleCard
          continueWithBase={Boolean(c.continueWithBase)}
          startDate={c.startDate}
          endDate={c.endDate}
          scheduleJson={data.scheduleList?.scheduleJson}
        />
      )}
      {(data.campaignPrompts ?? []).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Voice files</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-hidden rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Category</th>
                    <th className="px-3 py-2 text-left font-medium">File</th>
                    <th className="w-28 px-3 py-2 text-left font-medium">Duration (s)</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.campaignPrompts ?? []).map((p, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-3 py-2 capitalize">
                        {p.promptCategory || p.promptType || '—'}
                      </td>
                      <td className="px-3 py-2">
                        {p.fileName || p.promptName || `#${p.promptId ?? ''}`}
                      </td>
                      <td className="px-3 py-2">{p.promptDuration ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {(data.campaignAgents ?? []).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Patch agents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-hidden rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Agent group</th>
                    <th className="w-32 px-3 py-2 text-left font-medium">DTMF</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.campaignAgents ?? []).map((a, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-3 py-2">{a.groupName || `Group #${a.groupId}`}</td>
                      <td className="px-3 py-2 font-mono text-xs">{a.agentDtmf}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {isTts && sortedTtsRows.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>TTS sequence</CardTitle>
          </CardHeader>
          <CardContent>
            <TtsFlowPreviewPane
              values={ttsValues}
              onPatchRow={(i, patch) => {
                setPreviewRows(
                  ttsValues.ttsRows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)),
                );
              }}
            />
          </CardContent>
        </Card>
      )}

      {(data.campaignSms ?? []).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>SMS triggers</CardTitle>
            <CardDescription>SMS gateway endpoints fired on call events.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-hidden rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Event</th>
                    <th className="px-3 py-2 text-left font-medium">URL</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.campaignSms ?? []).map((s, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-3 py-2 capitalize">{s.smsEvent || '—'}</td>
                      <td className="break-all px-3 py-2 font-mono text-xs">{s.smsText || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </ScrollPage>
  );
}
